import type { EstimateAlgorithm, Floor } from '@wrp/shared';
import type { Point } from '../format';

export const WITHIN_M = 2;

export interface Sample {
  report_id: string;
  received_at: string;
  device_id: string;
  /** Estimated position, or null when the engine could not produce one. */
  estimate: Point | null;
  /** Operator-calibrated actual position; null until calibrated. */
  actual: Point | null;
  /** Loaded from a file: the estimate is a snapshot and is never recomputed. */
  frozen: boolean;
  /** Settings the estimate was computed with (live samples only). */
  key: string;
}

export interface EvalState {
  algorithm: EstimateAlgorithm;
  k: number;
  samples: Record<string, Sample>;
}

export const INITIAL_EVAL_STATE: EvalState = { algorithm: 'wknn', k: 4, samples: {} };

export function errorOf(sample: Sample): number | null {
  if (!sample.estimate || !sample.actual) return null;
  return Math.hypot(sample.estimate.x - sample.actual.x, sample.estimate.y - sample.actual.y);
}

export interface Performance {
  count: number;
  mean: number;
  median: number;
  rmse: number;
  max: number;
  withinPct: number;
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? (sorted[mid] ?? 0) : ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2;
}

/** Overall accuracy over the calibrated samples only; uncalibrated ones have no ground truth. */
export function computePerformance(samples: Sample[]): Performance | null {
  const errors = samples.map(errorOf).filter((e): e is number => e !== null);
  if (errors.length === 0) return null;
  const sum = errors.reduce((a, b) => a + b, 0);
  return {
    count: errors.length,
    mean: sum / errors.length,
    median: median(errors),
    rmse: Math.sqrt(errors.reduce((a, b) => a + b * b, 0) / errors.length),
    max: Math.max(...errors),
    withinPct: (errors.filter((e) => e <= WITHIN_M).length / errors.length) * 100,
  };
}

// ---------------------------------------------------------------------- file

const FILE_FORMAT = 'wifi-footprint-evaluation';
const FILE_VERSION = 1;

export interface EvaluationFile {
  format: typeof FILE_FORMAT;
  version: number;
  saved_at: string;
  floor: { id: string; name: string; width_m: number; height_m: number };
  algorithm: EstimateAlgorithm;
  k: number;
  summary: Performance | null;
  samples: Array<{
    report_id: string;
    received_at: string;
    device_id: string;
    estimate: Point | null;
    actual: Point | null;
    error_m: number | null;
  }>;
}

export function buildEvaluationFile(floor: Floor, state: EvalState, samples: Sample[]): EvaluationFile {
  return {
    format: FILE_FORMAT,
    version: FILE_VERSION,
    saved_at: new Date().toISOString(),
    floor: { id: floor.id, name: floor.name, width_m: floor.width_m, height_m: floor.height_m },
    algorithm: state.algorithm,
    k: state.k,
    summary: computePerformance(samples),
    samples: samples.map((s) => ({
      report_id: s.report_id,
      received_at: s.received_at,
      device_id: s.device_id,
      estimate: s.estimate,
      actual: s.actual,
      error_m: errorOf(s),
    })),
  };
}

function asPoint(value: unknown): Point | null {
  if (!value || typeof value !== 'object') return null;
  const { x, y } = value as Record<string, unknown>;
  return typeof x === 'number' && typeof y === 'number' && Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
}

/** Parses and validates a saved evaluation. Throws an Error with a readable message. */
export function parseEvaluationFile(text: string): {
  floorId: string;
  floorName: string;
  algorithm: EstimateAlgorithm | null;
  k: number | null;
  samples: Sample[];
} {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error('Not a valid JSON file');
  }
  const file = data as Partial<EvaluationFile> | null;
  if (!file || file.format !== FILE_FORMAT) throw new Error('Not a WiFi Footprint evaluation file');
  if (typeof file.version !== 'number' || file.version > FILE_VERSION) throw new Error('Unsupported evaluation file version');
  if (!Array.isArray(file.samples)) throw new Error('The file has no samples');

  const samples: Sample[] = [];
  for (const raw of file.samples) {
    if (!raw || typeof raw.report_id !== 'string') continue;
    samples.push({
      report_id: raw.report_id,
      received_at: typeof raw.received_at === 'string' ? raw.received_at : '',
      device_id: typeof raw.device_id === 'string' ? raw.device_id : '',
      estimate: asPoint(raw.estimate),
      actual: asPoint(raw.actual),
      frozen: true,
      key: '',
    });
  }
  const algorithm = file.algorithm === 'wknn' || file.algorithm === 'knn' || file.algorithm === 'multilateration' ? file.algorithm : null;
  return {
    floorId: file.floor?.id ?? '',
    floorName: file.floor?.name ?? '',
    algorithm,
    k: typeof file.k === 'number' && file.k >= 1 ? Math.round(file.k) : null,
    samples,
  };
}

export function downloadJson(filename: string, data: unknown): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
